import { socketMessageReceived, wsConnected, wsDisconnected } from "../websockets/actions"
import { WS_CONNECT, WS_DISCONNECT } from "../websockets/types"
import { getAuthHeaders } from "../websocket-utils"

const RECONNECT_BASE_DELAY_MS = 2000 // 2 seconds
const RECONNECT_MAX_DELAY_MS = 60000 // 60 seconds

const socketMiddleware = () => {
    let socket = null
    let retryCount = 0
    let reconnectTimer = null
    let lastIsGuest = false

    const onOpen = store => event => {
        retryCount = 0
        store.dispatch(wsConnected(event.target.url, socket))
    }
    const onClose = store => event => {
        // Capture URL synchronously — do not reference the event object inside the setTimeout callback.
        const url = event.currentTarget.url
        // Reconnect on unclean closes (abrupt drop) or server-side transient codes:
        //   1001 Going Away, 1011 Internal Error, 1012 Service Restart,
        //   1013 Try Again Later, 1014 Bad Gateway.
        // Code 1000 is a normal intentional close — do not reconnect.
        if (!event.wasClean || [1001, 1011, 1012, 1013, 1014].includes(event.code)) {
            const delay = Math.min(RECONNECT_BASE_DELAY_MS * Math.pow(2, retryCount), RECONNECT_MAX_DELAY_MS)
            retryCount++
            reconnectTimer = setTimeout(() => connectSocket(url, store, lastIsGuest, true), delay)
        } else {
            store.dispatch(wsDisconnected())
        }
    }
    const onMessage = store => event => {
        store.dispatch(socketMessageReceived(event))
    }

    const connectSocket = (host, store, isGuest = false, isReconnect = false) => {
        if (!isReconnect) {
            retryCount = 0
            lastIsGuest = isGuest
        }
        if (socket !== null) {
            // if we already have a socket open for the requested host, we don't need another one
            if (socket.url === host && socket.readyState === WebSocket.OPEN) {
                return
            }
            // if it's a new host, close the existing one and open a new one
            if (socket.url !== host) socket.close()
        }
        // connect to the remote host
        socket = new WebSocket(host, getAuthHeaders(isGuest))
        // websocket handlers
        socket.onclose = onClose(store)
        socket.onopen = onOpen(store)
        socket.onmessage = onMessage(store)
        socket.keepAlive = true
    }
    // the middleware part of this function
    return store => next => action => {
        switch (action.type) {
            case WS_CONNECT:
                if (!action.host) {
                    // if we don't have a host to connect to, we can't do anything
                    break
                }
                connectSocket(action.host, store, !!action.isGuest)
                break
            case WS_DISCONNECT:
                if (reconnectTimer !== null) {
                    clearTimeout(reconnectTimer)
                    reconnectTimer = null
                }
                if (socket !== null) {
                    socket.close()
                }
                socket = null
                break
            default:
                // this is middleware, so we need to pass the action along if it isn't the one we want
                return next(action)
        }
    }
}
export default socketMiddleware()
